import { useState, useEffect, useCallback } from 'react';
import serviceInstance from '../services/graficasPersonalizadasService';
import { message } from 'antd';

// Hook personalizado para gestionar gráficas personalizadas
export const useGraficasPersonalizadas = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados principales
  const [sensoresDisponibles, setSensoresDisponibles] = useState([]);
  const [graficasUsuario, setGraficasUsuario] = useState([]);
  const [datosGraficas, setDatosGraficas] = useState({});
  const [loadingGraficas, setLoadingGraficas] = useState({});
  const [erroresGraficas, setErroresGraficas] = useState({});
  
  // Estados del formulario
  const [showFormulario, setShowFormulario] = useState(false);
  const [graficaEditando, setGraficaEditando] = useState(null);
  
  // Estado de modo desarrollo
  const [modoDesarrollo, setModoDesarrollo] = useState(false);

  // Cargar sensores disponibles
  const cargarSensoresDisponibles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response;

      if (modoDesarrollo) {
        response = await serviceInstance.getSensoresPrueba();
      } else {
        response = await serviceInstance.getSensoresDisponibles();
      }

      if (response.success) {
        setSensoresDisponibles(response.data || []);
      } else {
        throw new Error(response.message || 'Error al cargar sensores');
      }
    } catch (err) {
      console.error('Error loading sensores:', err);
      setError(err.message || 'Error al cargar sensores disponibles');

      // Si no estamos en modo desarrollo, activarlo y cargar datos de prueba
      if (!modoDesarrollo) {
        setModoDesarrollo(true);
        try {
          const response = await serviceInstance.getSensoresPrueba();
          setSensoresDisponibles(response.data || []);
          setError(null); // Limpiar error si los datos de prueba se cargan correctamente
        } catch (fallbackErr) {
          console.error('Error loading fallback data:', fallbackErr);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [modoDesarrollo]);

  // Cargar gráficas del usuario
  const cargarGraficasUsuario = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response;

      if (modoDesarrollo) {
        response = await serviceInstance.getGraficasPrueba();
      } else {
        response = await serviceInstance.getGraficasUsuario();
      }

      if (response.success) {
        setGraficasUsuario(response.data || []);

        // Cargar datos para cada gráfica
        if (response.data && response.data.length > 0) {
          response.data.forEach(grafica => {
            cargarDatosGrafica(grafica._id, grafica.tipoGrafica);
          });
        }
      } else {
        throw new Error(response.message || 'Error al cargar gráficas');
      }
    } catch (err) {
      console.error('Error loading graficas usuario:', err);
      setError(err.message || 'Error al cargar gráficas del usuario');

      // Si no estamos en modo desarrollo, activarlo y cargar datos de prueba
      if (!modoDesarrollo) {
        setModoDesarrollo(true);
        try {
          const response = await serviceInstance.getGraficasPrueba();
          setGraficasUsuario(response.data || []);
          if (response.data && response.data.length > 0) {
            response.data.forEach(grafica => {
              cargarDatosGrafica(grafica._id, grafica.tipoGrafica);
            });
          }
          setError(null); // Limpiar error si los datos de prueba se cargan correctamente
        } catch (fallbackErr) {
          console.error('Error loading graficas prueba:', fallbackErr);
          setGraficasUsuario([]);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [modoDesarrollo]);

  // Cargar datos de una gráfica específica
  const cargarDatosGrafica = useCallback(async (graficaId, tipoGrafica) => {
    try {
      setLoadingGraficas(prev => ({ ...prev, [graficaId]: true }));
      setErroresGraficas(prev => ({ ...prev, [graficaId]: null }));

      let response;

      if (modoDesarrollo) {
        // Usar datos de prueba específicos para medidores
        response = await serviceInstance.getDatosPruebaMedidor(graficaId);
      } else {
        // Usar API real - solo promedio diario para tiempo real
        response = await serviceInstance.getDatosGrafica(graficaId, tipoGrafica, 'promedio_diario');
      }

      if (response.success) {
        setDatosGraficas(prev => ({
          ...prev,
          [graficaId]: response.data
        }));
      } else {
        throw new Error(response.message || 'Error al cargar datos de la gráfica');
      }
    } catch (err) {
      console.error(`Error loading datos grafica ${graficaId}:`, err);

      // Si no estamos en modo desarrollo y hay error, intentar con datos de prueba
      if (!modoDesarrollo) {
        try {
          const response = await serviceInstance.getDatosPruebaMedidor(graficaId);
          if (response.success) {
            setDatosGraficas(prev => ({
              ...prev,
              [graficaId]: response.data
            }));
            setErroresGraficas(prev => ({ ...prev, [graficaId]: null }));
            return; // Salir exitosamente
          }
        } catch (fallbackErr) {
          console.error(`Error loading fallback data for ${graficaId}:`, fallbackErr);
        }
      }

      setErroresGraficas(prev => ({
        ...prev,
        [graficaId]: err.message || 'Error al cargar datos'
      }));
    } finally {
      setLoadingGraficas(prev => ({ ...prev, [graficaId]: false }));
    }
  }, [modoDesarrollo]);

  // Crear nueva gráfica
  const crearGrafica = useCallback(async (datosGrafica) => {
    try {
      setLoading(true);
      
      let response;
      
      if (modoDesarrollo) {
        response = await serviceInstance.operacionPrueba('crear', datosGrafica);
      } else {
        response = await serviceInstance.crearGrafica(datosGrafica);
      }

      if (response.success) {
        message.success('Gráfica creada exitosamente');
        await cargarGraficasUsuario(); // Recargar lista
        setShowFormulario(false);
        setGraficaEditando(null);
        return true;
      } else {
        throw new Error(response.message || 'Error al crear la gráfica');
      }
    } catch (err) {
      console.error('Error creating grafica:', err);
      message.error(err.message || 'Error al crear la gráfica');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [modoDesarrollo, cargarGraficasUsuario]);

  // Actualizar gráfica existente
  const actualizarGrafica = useCallback(async (graficaId, datosGrafica) => {
    try {
      setLoading(true);
      
      let response;
      
      if (modoDesarrollo) {
        response = await serviceInstance.operacionPrueba('actualizar', { ...datosGrafica, id: graficaId });
      } else {
        response = await serviceInstance.actualizarGrafica(graficaId, datosGrafica);
      }

      if (response.success) {
        message.success('Gráfica actualizada exitosamente');
        await cargarGraficasUsuario(); // Recargar lista
        setShowFormulario(false);
        setGraficaEditando(null);
        return true;
      } else {
        throw new Error(response.message || 'Error al actualizar la gráfica');
      }
    } catch (err) {
      console.error('Error updating grafica:', err);
      message.error(err.message || 'Error al actualizar la gráfica');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [modoDesarrollo, cargarGraficasUsuario]);

  // Eliminar gráfica
  const eliminarGrafica = useCallback(async (graficaId) => {
    try {
      setLoading(true);
      
      let response;
      
      if (modoDesarrollo) {
        response = await serviceInstance.operacionPrueba('eliminar', { id: graficaId });
      } else {
        response = await serviceInstance.eliminarGrafica(graficaId);
      }
      
      if (response.success) {
        message.success('Gráfica eliminada exitosamente');
        await cargarGraficasUsuario(); // Recargar lista
        
        // Limpiar datos de la gráfica eliminada
        setDatosGraficas(prev => {
          const newData = { ...prev };
          delete newData[graficaId];
          return newData;
        });
        
        return true;
      } else {
        throw new Error(response.message || 'Error al eliminar la gráfica');
      }
    } catch (err) {
      console.error('Error deleting grafica:', err);
      message.error(err.message || 'Error al eliminar la gráfica');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [modoDesarrollo, cargarGraficasUsuario]);

  // Toggle estado activo de gráfica
  const toggleActivaGrafica = useCallback(async (graficaId, activa) => {
    try {
      setLoading(true);
      
      let response;
      
      if (modoDesarrollo) {
        response = await serviceInstance.operacionPrueba('toggle', { id: graficaId, activa });
      } else {
        response = await serviceInstance.toggleActivaGrafica(graficaId, activa);
      }

      if (response.success) {
        message.success(`Gráfica ${activa ? 'activada' : 'desactivada'} exitosamente`);
        await cargarGraficasUsuario(); // Recargar lista
        return true;
      } else {
        throw new Error(response.message || 'Error al cambiar el estado de la gráfica');
      }
    } catch (err) {
      console.error('Error toggling grafica:', err);
      message.error(err.message || 'Error al cambiar el estado de la gráfica');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [modoDesarrollo, cargarGraficasUsuario]);

  // Reordenar gráficas
  const reordenarGraficas = useCallback(async (graficasOrdenadas) => {
    try {
      setLoading(true);
      
      let response;
      
      if (modoDesarrollo) {
        response = await serviceInstance.operacionPrueba('reordenar', graficasOrdenadas);
      } else {
        response = await serviceInstance.reordenarGraficas(graficasOrdenadas);
      }

      if (response.success) {
        setGraficasUsuario(graficasOrdenadas);
        message.success('Orden actualizado exitosamente');
        return true;
      } else {
        throw new Error(response.message || 'Error al reordenar las gráficas');
      }
    } catch (err) {
      console.error('Error reordering graficas:', err);
      message.error(err.message || 'Error al reordenar las gráficas');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [modoDesarrollo]);

  // Actualizar datos de una gráfica específica
  const refreshGrafica = useCallback(async (graficaId, tipoGrafica) => {
    await cargarDatosGrafica(graficaId, tipoGrafica);
  }, [cargarDatosGrafica]);

  // Actualizar todos los datos
  const refresh = useCallback(async () => {
    await Promise.all([
      cargarSensoresDisponibles(),
      cargarGraficasUsuario()
    ]);
  }, [cargarSensoresDisponibles, cargarGraficasUsuario]);

  // Abrir formulario
  const abrirFormulario = useCallback((grafica = null) => {
    setGraficaEditando(grafica);
    setShowFormulario(true);
  }, []);

  // Cerrar formulario
  const cerrarFormulario = useCallback(() => {
    setShowFormulario(false);
    setGraficaEditando(null);
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatosIniciales = async () => {
      try {
        setInitialLoad(true);
        await Promise.all([
          cargarSensoresDisponibles(),
          cargarGraficasUsuario()
        ]);
      } catch (err) {
        console.error('Error loading initial data:', err);
      } finally {
        setInitialLoad(false);
      }
    };

    cargarDatosIniciales();
  }, [cargarSensoresDisponibles, cargarGraficasUsuario]);

  return {
    // Estados
    loading,
    initialLoad,
    error,
    sensoresDisponibles,
    graficasUsuario,
    datosGraficas,
    loadingGraficas,
    erroresGraficas,
    modoDesarrollo,
    showFormulario,
    graficaEditando,
    
    // Acciones
    crearGrafica,
    actualizarGrafica,
    eliminarGrafica,
    toggleActivaGrafica,
    reordenarGraficas,
    refresh,
    refreshGrafica,
    abrirFormulario,
    cerrarFormulario
  };
};